import { useEffect, useMemo } from "react";
import { matchPath, useLocation } from "react-router-dom";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  NOINDEX_PREFIXES,
  SEO_BY_ROUTE,
  SITE_NAME,
  type SeoPageConfig,
} from "./seoConfig";

function getSiteUrl() {
  const envUrl = import.meta.env.VITE_SITE_URL?.trim();
  if (envUrl) return envUrl.replace(/\/+$/, "");
  return window.location.origin.replace(/\/+$/, "");
}

function upsertMeta(name: string, content: string, attr: "name" | "property" = "name") {
  const selector = attr === "name" ? `meta[name="${name}"]` : `meta[property="${name}"]`;
  let tag = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let link = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", rel);
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function upsertJsonLd(id: string, payload: Record<string, unknown>) {
  let script = document.head.querySelector(
    `script[type="application/ld+json"][data-seo-id="${id}"]`,
  ) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.seoId = id;
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(payload);
}

function removeJsonLd(id: string) {
  const script = document.head.querySelector(
    `script[type="application/ld+json"][data-seo-id="${id}"]`,
  );
  if (script?.parentNode) {
    script.parentNode.removeChild(script);
  }
}

function resolveConfig(pathname: string): SeoPageConfig {
  const matched = SEO_BY_ROUTE.find((entry) =>
    Boolean(matchPath({ path: entry.pattern, end: true }, pathname)),
  );

  const isPrivatePath = NOINDEX_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

  if (matched) {
    return {
      ...matched.config,
      robots: matched.config.robots ?? (isPrivatePath ? "noindex,nofollow" : "index,follow"),
    };
  }

  return {
    title: `${SITE_NAME} | Healthcare Search and Booking`,
    description: DEFAULT_DESCRIPTION,
    robots: isPrivatePath ? "noindex,nofollow" : "index,follow",
    jsonLdType: "website",
  };
}

export default function SeoManager() {
  const location = useLocation();
  const siteUrl = useMemo(getSiteUrl, []);

  useEffect(() => {
    const pathname = location.pathname || "/";
    const pageUrl = `${siteUrl}${pathname}`;
    const config = resolveConfig(pathname);
    const title = config.title.includes(SITE_NAME)
      ? config.title
      : `${config.title} | ${SITE_NAME}`;

    document.title = title;

    upsertMeta("description", config.description || DEFAULT_DESCRIPTION);
    upsertMeta("robots", config.robots ?? "index,follow");
    upsertMeta("googlebot", config.robots ?? "index,follow");

    upsertMeta("og:type", "website", "property");
    upsertMeta("og:site_name", SITE_NAME, "property");
    upsertMeta("og:title", title, "property");
    upsertMeta("og:description", config.description || DEFAULT_DESCRIPTION, "property");
    upsertMeta("og:url", pageUrl, "property");
    upsertMeta("og:image", `${siteUrl}${DEFAULT_OG_IMAGE}`, "property");

    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", title);
    upsertMeta("twitter:description", config.description || DEFAULT_DESCRIPTION);
    upsertMeta("twitter:image", `${siteUrl}${DEFAULT_OG_IMAGE}`);

    upsertLink("canonical", pageUrl);

    const orgJsonLd = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE_NAME,
      url: siteUrl,
    };

    const websiteJsonLd = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE_NAME,
      url: siteUrl,
      potentialAction: {
        "@type": "SearchAction",
        target: `${siteUrl}/search/results?query={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    };

    upsertJsonLd("org", orgJsonLd);
    upsertJsonLd("website", websiteJsonLd);

    if (config.jsonLdType === "medicalWebPage") {
      upsertJsonLd("medical-page", {
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        name: title,
        description: config.description,
        url: pageUrl,
      });
    } else {
      removeJsonLd("medical-page");
    }
  }, [location.pathname, siteUrl]);

  return null;
}
